import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { Button, TextField, InputLabel, IconButton, Snackbar, Alert } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import mixpanel from 'mixpanel-browser';

import { Loader } from '../../../components/Elements/Loader';

import { downloadReportPDF, closeDownloadSuccessPdfToaster } from '../../../actions/partnerActions';

import {
  DownloadReportForm,
  downloadFreeTrialReport,
  pleaseConfirmInformation,
  pleaseVerifyInformationEntered,
  downloadReport,
  PDF_REPORT_SUCCESSFULLY_DOWNLOADED,
} from '../../../constants/Pages/FreeTrialReport';

import './FreeTrialDownloadForm.css';

export const FreeTrialDownloadForm = ({ freeTrialReport, loading, hasDownloadedPdf, user }) => {
  const [needVerifyInfo, setNeedVerifyInfo] = useState(false);

  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  useEffect(() => {
    if (hasDownloadedPdf) {
      reset();
    }
  }, [hasDownloadedPdf]);

  const handleFreeTrialDownloadForm = (val) => {
    const { lenderAdminEmail, googleAds } = freeTrialReport.templateData;
    const { totalClicks, totalImpressions } = googleAds;

    if (
      val['lender-admin-email'].toLowerCase() !== lenderAdminEmail.toLowerCase() ||
      totalClicks !== Number(val['google-ads-clicks']) ||
      totalImpressions !== Number(val['google-ads-impressions'])
    ) {
      mixpanel.track('Free Trial Download Form', {
        action: 'Need To Verify Info',
        financeLenderId: freeTrialReport.templateData.financeLenderId,
        freeTrialId: freeTrialReport.templateData.id,
      });
      setNeedVerifyInfo(true);
      return;
    }

    mixpanel.track('Free Trial Download Form', {
      action: 'Download PDF Report',
      financeLenderId: freeTrialReport.templateData.financeLenderId,
      freeTrialId: freeTrialReport.templateData.id,
    });

    dispatch(
      downloadReportPDF({
        financeLenderId: freeTrialReport.templateData.financeLenderId,
        templateData: freeTrialReport.templateData,
        user: { ...(user || {}), anonymousUserId: mixpanel.get_distinct_id() },
      }),
    );
  };

  const handleCloseVerifyInfo = () => {
    setNeedVerifyInfo(false);
  };

  return (
    <div className="download-free-trial-report-wrapper">
      <h2 className="free-trial-download-form-header">{downloadFreeTrialReport}</h2>
      <h6 className="free-trial-download-form-subheader">{pleaseConfirmInformation}</h6>

      {needVerifyInfo && (
        <div className="need-verify-information-wrapper">
          <div className="need-verify-information-entered-text">
            {' '}
            {pleaseVerifyInformationEntered}{' '}
          </div>
          <div className="close-icon-button-wrapper">
            <IconButton
              onClick={handleCloseVerifyInfo}
              sx={{
                '&:hover': { backgroundColor: 'black', color: 'white' },
                backgroundColor: 'black',
                color: 'white',
              }}
            >
              <CloseIcon />
            </IconButton>
          </div>
        </div>
      )}

      <form className="free-trial-download-form">
        {DownloadReportForm.map(({ name, placeholder, required, pattern, type, label }) => {
          return (
            <div key={name} className="free-trial-download-input-wrapper">
              <InputLabel sx={{ color: 'gray', mb: 1, fontSize: '14px', fontFamily: 'sans-serif' }}>
                {label}
              </InputLabel>
              <TextField
                className={name}
                name={name}
                error={!!errors[name]}
                helperText={errors[name]?.message}
                placeholder={placeholder}
                variant="outlined"
                autoComplete="off"
                type={type}
                sx={{
                  backgroundColor: 'white',
                  borderRadius: '10px',
                  border: '1px solid gray',
                }}
                {...register(name, {
                  required,
                  pattern,
                })}
                disabled={loading}
              />{' '}
            </div>
          );
        })}

        <div>
          <Button
            sx={{
              '&:disabled': { color: 'white', opacity: 0.4 },
              m: 0,
              color: 'white',
              backgroundColor: 'black',
              width: '100%',
              borderRadius: 2,
              height: '56px',
            }}
            disabled={loading || !freeTrialReport}
            type="button"
            onClick={handleSubmit(handleFreeTrialDownloadForm)}
          >
            {loading ? <Loader /> : downloadReport}
          </Button>
        </div>
        <Snackbar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          open={hasDownloadedPdf}
          onClose={(event, reason) => {
            if (reason === 'clickaway') {
              return;
            }

            // close the snackbar
            dispatch(() => closeDownloadSuccessPdfToaster(dispatch));
            return;
          }}
          key={'top' + 'center'}
          sx={{
            borderRadius: '100px',
          }}
          autoHideDuration={8000}
        >
          <Alert
            severity="success"
            variant="filled"
            sx={{ width: '100%', borderRadius: '5px' }}
            action={
              <IconButton
                size="small"
                sx={{
                  '&:hover': { backgroundColor: 'black', color: 'white' },
                  backgroundColor: 'black',
                  color: 'white',
                }}
                onClick={() => dispatch(() => closeDownloadSuccessPdfToaster(dispatch))}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          >
            {PDF_REPORT_SUCCESSFULLY_DOWNLOADED}
          </Alert>
        </Snackbar>
      </form>
    </div>
  );
};
